// Pure Sentry event/envelope construction (leaf module — ES2022 only, no
// `fetch`/`crypto`/`URL`/`window`, so it stays in @pageboss/shared's
// pure-types/constants charter and is node:test-able). The impure send (the
// `fetch`, the random event id, the clock) lives in each worker/SPA's tiny
// `lib/sentry.ts` wrapper — mirroring the per-worker `email.ts` precedent.
// Deliberately NOT @sentry/* — consistent with the repo's self-rolled,
// minimal-dependency posture (self-rolled Stripe webhook + OAuth crypto).

export type SentryLevel = 'fatal' | 'error' | 'warning' | 'info';

export interface CaptureContext {
  environment?: string;
  release?: string;
  /** Which surface raised it — e.g. 'pageboss-jobs', 'dashboard-spa'. */
  serverName?: string;
  /** Route or cron job name. */
  transaction?: string;
  level?: SentryLevel;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

export interface ParsedDsn {
  envelopeUrl: string;
  publicKey: string;
  projectId: string;
}

export interface SentryRequest {
  url: string;
  headers: Record<string, string>;
  body: string;
}

// DSN shape: `https://<publicKey>@<host>/<projectId>`. Parsed with a regex (not
// `URL`, which isn't in the ES2022 lib this package compiles against).
const DSN_RE = /^(https?):\/\/([^@/]+)@([^/]+)\/(.+)$/i;

export function parseDsn(dsn: string): ParsedDsn | null {
  const m = DSN_RE.exec(dsn.trim());
  if (!m) return null;
  const [, protocol, publicKey, host, projectId] = m;
  if (!publicKey || !projectId) return null;
  return {
    envelopeUrl: `${protocol}://${host}/api/${projectId}/envelope/`,
    publicKey,
    projectId,
  };
}

function errorParts(err: unknown): { type: string; value: string; stack?: string } {
  if (err instanceof Error) {
    const out: { type: string; value: string; stack?: string } = {
      type: err.name || 'Error',
      value: err.message || String(err),
    };
    if (err.stack) out.stack = err.stack;
    return out;
  }
  if (typeof err === 'string') return { type: 'Error', value: err };
  try {
    return { type: 'Error', value: JSON.stringify(err) };
  } catch {
    return { type: 'Error', value: String(err) };
  }
}

// Build a Sentry event payload. `eventId` (32 hex, no dashes) and `timestampMs`
// are injected so this stays pure/deterministic. The stack is carried as
// `extra.stack` (a string) rather than parsed frames — simple + robust; Sentry
// groups by exception type+value.
export function buildSentryEvent(
  err: unknown,
  ctx: CaptureContext,
  eventId: string,
  timestampMs: number,
): Record<string, unknown> {
  const { type, value, stack } = errorParts(err);
  const extra = { ...(ctx.extra ?? {}), ...(stack ? { stack } : {}) };
  const event: Record<string, unknown> = {
    event_id: eventId,
    timestamp: timestampMs / 1000,
    platform: 'javascript',
    level: ctx.level ?? 'error',
    exception: { values: [{ type, value }] },
  };
  if (ctx.environment) event['environment'] = ctx.environment;
  if (ctx.release) event['release'] = ctx.release;
  if (ctx.serverName) event['server_name'] = ctx.serverName;
  if (ctx.transaction) event['transaction'] = ctx.transaction;
  if (ctx.tags && Object.keys(ctx.tags).length) event['tags'] = ctx.tags;
  if (Object.keys(extra).length) event['extra'] = extra;
  return event;
}

// A Sentry envelope is newline-delimited JSON: envelope header, item header,
// item payload.
export function buildEnvelope(event: Record<string, unknown>, sentAtMs: number): string {
  const header = JSON.stringify({ event_id: event['event_id'], sent_at: new Date(sentAtMs).toISOString() });
  const itemHeader = JSON.stringify({ type: 'event' });
  const item = JSON.stringify(event);
  return `${header}\n${itemHeader}\n${item}\n`;
}

// Build the full HTTP request (url + headers + body) to POST an error to
// Sentry, or null if the DSN is missing/invalid (disabled-safe). The caller
// supplies a random `eventId` and the clock, then does the `fetch`.
export function buildSentryRequest(
  dsn: string | undefined,
  err: unknown,
  ctx: CaptureContext,
  eventId: string,
  timestampMs: number,
): SentryRequest | null {
  if (!dsn) return null;
  const parsed = parseDsn(dsn);
  if (!parsed) return null;
  const event = buildSentryEvent(err, ctx, eventId, timestampMs);
  return {
    url: parsed.envelopeUrl,
    headers: {
      'content-type': 'application/x-sentry-envelope',
      'x-sentry-auth': `Sentry sentry_version=7, sentry_key=${parsed.publicKey}, sentry_client=pageboss-lite/1.0`,
    },
    body: buildEnvelope(event, timestampMs),
  };
}
